import * as RadixSelect from "@radix-ui/react-select";
import { CaretDown, Check } from "@phosphor-icons/react";
import { forwardRef, Fragment, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import { floatingCollisionPadding, floatingLayerClassName, selectContentCollisionClassName } from "@/components/ui/overlays/overlayPositioning";

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional group heading. Options sharing a `group` render under one heading,
   * in first-seen group order. Options without a group render ungrouped. */
  group?: string;
};

/**
 * Controls the option popover width relative to the trigger.
 * - `"trigger"` — exactly the trigger width.
 * - `"auto"` — at least the trigger width, growing to fit content (can be wider).
 * - a number — a fixed pixel width.
 */
export type SelectMenuWidth = "trigger" | "auto" | number;

/** Groups a flat option list by `group`, preserving first-seen order. */
export function groupSelectOptions(
  options: SelectOption[]
): { group?: string; items: SelectOption[] }[] {
  const groups: { group?: string; items: SelectOption[] }[] = [];
  const indexByGroup = new Map<string | undefined, number>();
  for (const option of options) {
    const key = option.group;
    if (!indexByGroup.has(key)) {
      indexByGroup.set(key, groups.length);
      groups.push({ group: key, items: [] });
    }
    groups[indexByGroup.get(key)!].items.push(option);
  }
  return groups;
}

export type SelectProps = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Option popover width relative to the trigger. Defaults to `"auto"`. */
  menuWidth?: SelectMenuWidth;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md";
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      placeholder = "Select",
      open: controlledOpen,
      defaultOpen = false,
      onOpenChange,
      menuWidth = "auto",
      error,
      size = "md",
      className,
      id,
      value,
      defaultValue,
      onValueChange,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const menuWidthClass =
      menuWidth === "trigger"
        ? "bfrs-w-[var(--radix-select-trigger-width)]"
        : typeof menuWidth === "number"
          ? ""
          : "bfrs-min-w-[var(--radix-select-trigger-width)]";
    const menuWidthStyle = typeof menuWidth === "number" ? { width: menuWidth, minWidth: menuWidth } : undefined;
    const renderItem = (option: SelectOption) => (
      <RadixSelect.Item
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        className={cn(
          "bfrs-flex bfrs-w-full bfrs-cursor-pointer bfrs-select-none bfrs-items-center bfrs-justify-between bfrs-gap-3 bfrs-rounded-sm bfrs-text-text-primary bfrs-outline-none data-[disabled]:bfrs-pointer-events-none data-[disabled]:bfrs-cursor-default data-[highlighted]:bfrs-bg-primary-soft data-[disabled]:bfrs-text-text-disabled data-[state=checked]:bfrs-bg-primary-soft data-[state=checked]:bfrs-font-bold",
          "bfrs-h-7 bfrs-px-2.5 bfrs-py-1.5 bfrs-text-[12px]"
        )}
      >
        <RadixSelect.ItemText className="bfrs-min-w-0 bfrs-truncate">{option.label}</RadixSelect.ItemText>
        <span className="bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-text-primary">
          <RadixSelect.ItemIndicator>
            <Check size={13} weight="bold" />
          </RadixSelect.ItemIndicator>
        </span>
      </RadixSelect.Item>
    );
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<string | undefined>(defaultValue);
    const currentValue = isControlled ? value : internalValue;
    const selectedLabel = options.find((option) => option.value === currentValue)?.label;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const currentOpen = controlledOpen ?? uncontrolledOpen;

    const valueRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const node = valueRef.current;
      if (!node || typeof globalThis.ResizeObserver === "undefined") return;
      const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth + 1);
      measure();
      const observer = new globalThis.ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }, [selectedLabel]);

    const handleValueChange = (next: string) => {
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
    };

    const handleOpenChange = (nextOpen: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    };

    const trigger = (
      <RadixSelect.Trigger
        ref={ref}
        id={id}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid ?? error}
        data-filled={currentValue && !error ? "" : undefined}
        className={cn(
          "bfrs-flex bfrs-w-full bfrs-items-center bfrs-justify-between bfrs-gap-2 bfrs-rounded-lg bfrs-border bfrs-bg-surface-card bfrs-text-left bfrs-text-text-primary bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20 data-[state=open]:bfrs-ring-2 data-[state=open]:bfrs-ring-border-focus/20 aria-[expanded=true]:bfrs-ring-2 aria-[expanded=true]:bfrs-ring-border-focus/20 disabled:bfrs-cursor-not-allowed disabled:bfrs-bg-surface-app disabled:bfrs-text-text-disabled data-[placeholder]:bfrs-text-text-placeholder",
          cn("bfrs-rounded-[7px] bfrs-px-3 bfrs-text-[12px]", size === "sm" ? "bfrs-h-7" : "bfrs-h-9"),
          error
            ? "bfrs-border-danger focus-visible:bfrs-border-danger focus-visible:bfrs-ring-danger/15 data-[state=open]:bfrs-border-danger data-[state=open]:bfrs-ring-danger/15 aria-[expanded=true]:bfrs-border-danger aria-[expanded=true]:bfrs-ring-danger/15"
            : cn("bfrs-border-border focus-visible:bfrs-border-border-focus data-[state=open]:bfrs-border-border-focus aria-[expanded=true]:bfrs-border-border-focus", size === "sm" && "data-[filled]:bfrs-border-border-focus"),
          className
        )}
      >
        <span ref={valueRef} className="bfrs-block bfrs-min-w-0 bfrs-flex-1 bfrs-truncate">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon className="bfrs-shrink-0 bfrs-text-text-muted">
          <CaretDown className={cn("bfrs-transition-transform bfrs-duration-150", currentOpen && "bfrs-rotate-180")} size={10} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
    );

    return (
      <RadixSelect.Root value={value} defaultValue={defaultValue} open={currentOpen} onOpenChange={handleOpenChange} onValueChange={handleValueChange} {...props}>
        {selectedLabel ? (
          <Tooltip content={selectedLabel} disabled={!isTruncated}>
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )}
        <RadixSelect.Portal>
        <BfrsPortalScope>
          <RadixSelect.Content
            position="popper"
            sideOffset={6}
            collisionPadding={floatingCollisionPadding}
            style={menuWidthStyle}
            className={cn(
              floatingLayerClassName,
              "bfrs-overflow-hidden bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-elevated bfrs-animate-content-in",
              menuWidthClass,
              selectContentCollisionClassName
            )}
          >
            <RadixSelect.Viewport className="bfrs-p-1">
              {groupSelectOptions(options).map((grp, groupIndex) =>
                grp.group ? (
                  <RadixSelect.Group key={`group-${groupIndex}`}>
                    <RadixSelect.Label className="bfrs-px-3 bfrs-pb-1 bfrs-pt-2 bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.08em] bfrs-text-text-muted">
                      {grp.group}
                    </RadixSelect.Label>
                    {grp.items.map(renderItem)}
                  </RadixSelect.Group>
                ) : (
                  <Fragment key={`group-${groupIndex}`}>{grp.items.map(renderItem)}</Fragment>
                )
              )}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </BfrsPortalScope>
      </RadixSelect.Portal>
    </RadixSelect.Root>
    );
  }
);

Select.displayName = "Select";
